/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { getApi, postApi } from 'views/services/api';

const SUGGESTED_TYPES = ['Estándar', 'Doble', 'Matrimonial', 'Suite', 'Familiar'];

// Genera numeración por tipo evitando números ya usados:
// tipo 1 → 101, 102...; tipo 2 → 201, 202...
const generateNumbers = (rows, takenSet) => {
  const taken = new Set(takenSet);
  return rows.map((row, i) => {
    const base = (i + 1) * 100;
    const numbers = [];
    let n = 1;
    while (numbers.length < row.quantity) {
      const candidate = String(base + n);
      if (!taken.has(candidate)) {
        numbers.push(candidate);
        taken.add(candidate);
      }
      n++;
      if (n > 998) break; // tope de seguridad
    }
    return { ...row, numbers };
  });
};

export default function StepRooms({ hotelId, onNext, onBack }) {
  const [phase, setPhase] = useState('types'); // 'types' | 'numbers'
  const [rows, setRows] = useState([{ name: '', quantity: '' }]);
  const [plan, setPlan] = useState([]); // [{name, quantity, numbers: []}]
  const [existingRooms, setExistingRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const res = await getApi(`api/room/viewallrooms/${hotelId}`);
        if (res?.status === 200 && Array.isArray(res.data)) {
          setExistingRooms(res.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchExisting();
  }, [hotelId]);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = (name = '') => setRows((prev) => [...prev, { name, quantity: '' }]);

  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const addSuggested = (name) => {
    const used = rows.map((r) => r.name.trim().toLowerCase());
    if (used.includes(name.toLowerCase())) return;
    // Rellena la primera fila vacía o agrega una nueva
    const emptyIndex = rows.findIndex((r) => !r.name.trim());
    if (emptyIndex >= 0) {
      updateRow(emptyIndex, 'name', name);
    } else {
      addRow(name);
    }
  };

  const goToNumbers = () => {
    const validRows = rows
      .map((r) => ({ name: r.name.trim(), quantity: parseInt(r.quantity, 10) || 0 }))
      .filter((r) => r.name && r.quantity > 0);

    if (validRows.length === 0) {
      toast.error('Agrega al menos un tipo de habitación con su cantidad');
      return;
    }
    const names = validRows.map((r) => r.name.toLowerCase());
    if (new Set(names).size !== names.length) {
      toast.error('Hay tipos de habitación repetidos');
      return;
    }
    const totalRooms = validRows.reduce((a, r) => a + r.quantity, 0);
    if (totalRooms > 300) {
      toast.error('El máximo en este asistente es 300 habitaciones');
      return;
    }

    const takenNumbers = existingRooms.map((r) => String(r.roomNo));
    setPlan(generateNumbers(validRows, takenNumbers));
    setPhase('numbers');
  };

  const updateNumber = (typeIndex, numIndex, value) => {
    setPlan((prev) =>
      prev.map((t, i) => (i === typeIndex ? { ...t, numbers: t.numbers.map((n, j) => (j === numIndex ? value : n)) } : t))
    );
  };

  const createRooms = async () => {
    // Validación local de números
    const allNumbers = plan.flatMap((t) => t.numbers.map((n) => String(n).trim()));
    if (allNumbers.some((n) => !n)) {
      toast.error('Hay habitaciones sin número');
      return;
    }
    if (new Set(allNumbers).size !== allNumbers.length) {
      toast.error('Hay números de habitación repetidos');
      return;
    }

    setSaving(true);
    try {
      // 1. Crear (o reutilizar) los tipos de habitación
      const typesRes = await getApi(`api/room/room-types/list/${hotelId}`);
      const existingTypes = typesRes?.status === 200 && Array.isArray(typesRes.data) ? typesRes.data : [];

      const typeIds = {};
      for (const t of plan) {
        const match = existingTypes.find((et) => et.name.trim().toLowerCase() === t.name.toLowerCase());
        if (match) {
          typeIds[t.name] = match._id;
        } else {
          const res = await postApi('api/room/room-types/add', { hotelId, name: t.name });
          if (res?.status === 200 && res.data?._id) {
            typeIds[t.name] = res.data._id;
          } else {
            toast.error(res?.response?.data?.error || `No se pudo crear el tipo "${t.name}"`);
            setSaving(false);
            return;
          }
        }
      }

      // 2. Crear las habitaciones en bloque
      const roomsPayload = plan.flatMap((t) =>
        t.numbers.map((n) => ({
          roomNo: String(n).trim(),
          roomType: t.name,
          roomTypeId: typeIds[t.name]
        }))
      );

      const bulkRes = await postApi('api/room/bulk-add', { hotelId, rooms: roomsPayload });
      if (bulkRes?.status === 200) {
        toast.success(`${bulkRes.data.count} habitaciones creadas`);
        onNext();
      } else {
        toast.error(bulkRes?.response?.data?.error || 'No se pudieron crear las habitaciones');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error creando las habitaciones');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  // ============ FASE 1: Tipos y cantidades ============
  if (phase === 'types') {
    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            ¿Qué tipos de habitación tiene tu hotel?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Escribe cada tipo y cuántas habitaciones tienes de cada uno. Puedes usar las sugerencias.
          </Typography>
        </Box>

        {existingRooms.length > 0 && (
          <Alert severity="info">
            Ya tienes {existingRooms.length} habitaciones creadas. Aquí solo agrega tipos y habitaciones nuevas, o pulsa
            &quot;Continuar sin crear más&quot;.
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {SUGGESTED_TYPES.map((s) => (
            <Chip key={s} label={s} onClick={() => addSuggested(s)} clickable variant="outlined" color="primary" />
          ))}
        </Stack>

        <Stack spacing={2}>
          {rows.map((row, i) => (
            <Grid container spacing={2} key={i} alignItems="center">
              <Grid item xs={6} sm={7}>
                <TextField
                  fullWidth
                  label={`Tipo de habitación ${i + 1}`}
                  placeholder="Ej: Estándar"
                  value={row.name}
                  onChange={(e) => updateRow(i, 'name', e.target.value)}
                />
              </Grid>
              <Grid item xs={4} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="¿Cuántas?"
                  inputProps={{ min: 1, max: 300 }}
                  value={row.quantity}
                  onChange={(e) => updateRow(i, 'quantity', e.target.value)}
                />
              </Grid>
              <Grid item xs={2} sm={1}>
                <IconButton onClick={() => removeRow(i)} disabled={rows.length === 1} color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>

        <Box>
          <Button startIcon={<AddIcon />} onClick={() => addRow()} sx={{ textTransform: 'none' }}>
            Agregar otro tipo
          </Button>
        </Box>

        <Divider />

        <Stack direction="row" justifyContent="space-between">
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }}>
            Atrás
          </Button>
          <Stack direction="row" spacing={2}>
            {existingRooms.length > 0 && (
              <Button variant="outlined" onClick={onNext} sx={{ textTransform: 'none' }}>
                Continuar sin crear más
              </Button>
            )}
            <Button variant="contained" onClick={goToNumbers} sx={{ px: 4, fontWeight: 600 }}>
              Continuar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  // ============ FASE 2: Numeración editable ============
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Revisa los números de habitación
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generamos una numeración sugerida. Puedes cambiar cualquier número antes de crear las habitaciones (ej: &quot;A1&quot;,
          &quot;Cabaña 3&quot;).
        </Typography>
      </Box>

      {plan.map((t, ti) => (
        <Box key={t.name}>
          <Typography variant="h6" gutterBottom>
            {t.name} · {t.quantity} habitaciones
          </Typography>
          <Grid container spacing={1.5}>
            {t.numbers.map((n, ni) => (
              <Grid item xs={4} sm={2.4} key={ni}>
                <TextField size="small" fullWidth value={n} onChange={(e) => updateNumber(ti, ni, e.target.value)} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Divider />

      <Stack direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBackIcon />} onClick={() => setPhase('types')} sx={{ textTransform: 'none' }} disabled={saving}>
          Atrás
        </Button>
        <Button variant="contained" onClick={createRooms} disabled={saving} sx={{ px: 4, fontWeight: 600 }}>
          {saving ? 'Creando...' : 'Crear habitaciones'}
        </Button>
      </Stack>
    </Stack>
  );
}
